import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import PublicBrandHeader from "@/components/PublicBrandHeader";

// Mirror of /app/backend/security.py:_PASSWORD_RE — keep in sync.
const checkStrength = (pw) => ({
  length: pw.length >= 12,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  digit: /\d/.test(pw),
});

export default function ResetPasswordPage() {
  useDocumentTitle("Reset password", "Choose a new password to regain access.");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const strength = useMemo(() => checkStrength(password), [password]);
  const allMet = Object.values(strength).every(Boolean);
  const match = confirm.length > 0 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Reset token is missing from the URL. Please use the link from your email."); return; }
    if (!allMet) { setError("Password doesn't meet the requirements below."); return; }
    if (!match) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post(`/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 400 && /token/i.test(detail)) {
        setError("This reset link is invalid or has expired. Please request a new one.");
      } else if (status === 400) {
        setError(detail || "Password doesn't meet the requirements.");
      } else {
        setError("Couldn't reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-[420px]">
          <PublicBrandHeader subtitle="Account recovery" />
          <div className="bg-white border border-border rounded-md p-7 text-center" data-testid="reset-success">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 size={24} className="text-emerald-600" />
            </div>
          </div>
          <h1 className="font-heading font-bold text-xl tracking-tight mb-2">Password reset</h1>
          <p className="text-sm text-muted-foreground mb-4">Redirecting you to sign in…</p>
          <Link to="/login"><Button variant="outline" className="w-full">Sign in now</Button></Link>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="reset-password-page">
      <div className="w-full max-w-[420px]">
        <PublicBrandHeader subtitle="Account recovery" />
        <div className="bg-white border border-border rounded-md p-7 shadow-sm">
        <div className="flex justify-center mb-4">
          <div className="w-10 h-10 rounded-full bg-slate-100 border border-border flex items-center justify-center">
            <Lock size={18} className="text-slate-700" />
          </div>
        </div>
        <h1 className="font-heading font-bold text-2xl tracking-tight mb-1 text-center">Set a new password</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">
          Choose a password that's at least 12 characters and mixes letters and numbers.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-medium">New password</Label>
            <div className="relative mt-1">
              <Input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="reset-password-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium">Confirm password</Label>
            <Input
              type={showPass ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1"
              data-testid="reset-confirm-input"
            />
            {confirm && !match && <p className="text-xs text-red-600 mt-1">Passwords don't match</p>}
          </div>

          <ul className="text-xs space-y-1 bg-slate-50 border border-border rounded-md p-3" data-testid="password-checklist">
            <li className={strength.length ? "text-emerald-700" : "text-muted-foreground"}>{strength.length ? "✓" : "○"} At least 12 characters</li>
            <li className={strength.upper ? "text-emerald-700" : "text-muted-foreground"}>{strength.upper ? "✓" : "○"} One uppercase letter</li>
            <li className={strength.lower ? "text-emerald-700" : "text-muted-foreground"}>{strength.lower ? "✓" : "○"} One lowercase letter</li>
            <li className={strength.digit ? "text-emerald-700" : "text-muted-foreground"}>{strength.digit ? "✓" : "○"} One number</li>
          </ul>

          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading || !allMet || !match} data-testid="reset-submit-btn">
            {loading ? "Resetting…" : "Reset password"}
          </Button>
        </form>
        </div>
      </div>
    </div>
  );
}
